import { DEFAULT_APP_NAME } from "@template/types";

export interface AppConfig {
  apiBaseUrl: string;
  appName: string;
  showDemoCredentials: boolean;
}

interface RuntimeConfigWindowShape {
  API_BASE_URL?: string;
  APP_NAME?: string;
  SHOW_DEMO_CREDENTIALS?: string | boolean;
}

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfigWindowShape;
  }
}

function toBoolean(value: string | boolean | undefined, fallback: boolean): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.toLowerCase() === "true";
  return fallback;
}

/**
 * Resolves app configuration with the following precedence:
 * 1. `window.__RUNTIME_CONFIG__` (populated by docker-entrypoint.sh at
 *    container boot from container env vars) — lets one built Docker image
 *    be reconfigured per environment without rebuilding.
 * 2. Vite's build-time `import.meta.env.VITE_*` vars — used in local dev.
 * 3. Hardcoded defaults.
 */
export function getRuntimeConfig(): AppConfig {
  const runtime = window.__RUNTIME_CONFIG__ ?? {};
  const hasRuntimeValues = Object.keys(runtime).length > 0;

  const apiBaseUrl = hasRuntimeValues
    ? (runtime.API_BASE_URL ?? "")
    : (import.meta.env.VITE_API_BASE_URL ?? "http://localhost:3000");

  const appName = hasRuntimeValues
    ? (runtime.APP_NAME ?? DEFAULT_APP_NAME)
    : (import.meta.env.VITE_APP_NAME ?? DEFAULT_APP_NAME);

  const showDemoCredentials = hasRuntimeValues
    ? toBoolean(runtime.SHOW_DEMO_CREDENTIALS, false)
    : toBoolean(import.meta.env.VITE_SHOW_DEMO_CREDENTIALS, false);

  return {
    apiBaseUrl: apiBaseUrl || "http://localhost:3000",
    appName: appName || DEFAULT_APP_NAME,
    showDemoCredentials,
  };
}
